import React from 'react';
import axios from 'axios';
import {
    Form,
    Button,
    Message,
    Table,
    Loader,
    Grid,
    Popup,
    Icon,
} from 'semantic-ui-react';
import { fetchFaculties, fetchStudyCycle } from '../../Lookups/util';
import SearchTableData from '../../../views/util/SearchTableData';
import PaginationFront from '../../../views/util/PaginationFront';
import ReactExport from 'react-data-export';
import ReportExcelFile from './ReportExcelFile';
import { sanitizeExcelDataSet } from '../../util/sanitizeExcel';
import { FormattedMessage } from 'react-intl';

const ExcelFile = ReportExcelFile;
const { ExcelSheet } = ReactExport.ExcelFile;

axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.withCredentials = true;

class StudyProgramsByCycle extends React.Component {
    state = {
        studyCycles: [],
        organizationalUnits: [],

        selectedCycleIds: [],
        selectedOrganizationalUnitId: null,

        data: [],
        loading: false,
        error: false,

        currentPage: 1,
        postsPerPage: 10,

        filteredData: [],
    };

    componentDidMount() {
        const { organizationalUnits = [] } = this.props;

        var fetchAdditionalData = false;
        if (organizationalUnits.length === 0) fetchAdditionalData = true;

        this.setState({
            organizationalUnits: organizationalUnits.map((el) => ({
                key: el.id,
                value: el.id,
                text: el.name,
            })),
        });

        this.fetchInitialData(fetchAdditionalData);
    }

    fetchInitialData = async (fetchAdditionalData = false) => {
        this.setState({ loading: true }); // start loading

        try {
            const studyCycleResponse = await fetchStudyCycle();
            const studyCycles = studyCycleResponse.data.map((el) => ({
                key: el.id,
                value: el.id,
                text: el.displayName,
            }));

            let organizationalUnits = this.state.organizationalUnits;

            if (fetchAdditionalData) {
                const [facultiesResponse] = await Promise.all([fetchFaculties()]);

                organizationalUnits = facultiesResponse.data.map((el) => ({
                    key: el.id,
                    value: el.id,
                    text: el.name,
                }));
            }

            this.setState({
                studyCycles,
                organizationalUnits,
                loading: false,
            });
        } catch (error) {
            console.error('Error fetching initial data:', error);
            this.setState({
                loading: false,
                error: true
            });
        }
    };

    handleCycleChange = (e, { value }) => {
        this.setState({ selectedCycleIds: value });
    };

    fetchData = async () => {
        const {
            selectedCycleIds,
            selectedOrganizationalUnitId
        } = this.state;
        if (selectedCycleIds.length === 0) {
            return;
        }
        this.setState({
            loading: true,
            data: [],
            filteredData: [],
            currentPage: 1,
        });
        try {
            const response = await axios.get(
                `/reports/study-programs-by-study-cycles-for-faculty/${selectedOrganizationalUnitId}?studyCycleIds=${selectedCycleIds.join(
                    ','
                )}`
            );
            this.setState({
                data: response.data,
                filteredData: response.data,
                loading: false,
                currentPage: 1,
            });
        } catch (error) {
            console.error('Error fetching study programs by cycles:', error);
            this.setState({
                loading: false,
                error: true
            });
        }
    };

    getResults = (results) => {
        if (results.length === 0) {
            this.setState({
                filteredData: this.state.data,
                currentPage: 1
            });
        } else {
            this.setState({
                filteredData: results,
                currentPage: 1
            });
        }
    };

    paginate = (e, { activePage }) => {
        this.setState({ currentPage: activePage });
    };

    render() {
        const {
            selectedCycleIds,
            selectedOrganizationalUnitId,
            data,
            loading,
            organizationalUnits,
            studyCycles,
            filteredData,
            currentPage,
            postsPerPage,
        } = this.state;

        const indexOfLastPost = currentPage * postsPerPage;
        const indexOfFirstPost = indexOfLastPost - postsPerPage;
        const currentPosts = filteredData.slice(indexOfFirstPost, indexOfLastPost);

        const multiDataSet = [
            {
                columns: [
                    {
                        title: 'Fakultet / akademija - ID',
                        width: { wpx: 80 }
                    },
                    {
                        title: 'Fakultet / akademija',
                        width: { wch: 30 }
                    },
                    {
                        title: 'Odsjek - ID',
                        width: { wpx: 80 }
                    },
                    {
                        title: 'Odsjek',
                        width: { wch: 30 }
                    },
                    {
                        title: 'Studijski program - ID',
                        width: { wch: 15 }
                    },
                    {
                        title: 'Studijski program',
                        width: { wch: 75 }
                    },
                    {
                        title: 'NPP',
                        width: { wch: 40 }
                    },
                    {
                        title: 'Ciklus studija',
                        width: { wch: 30 }
                    },
                    {
                        title: 'Trajanje (semestri / godine)',
                        width: { wch: 25 }
                    },
                    {
                        title: 'language',
                        width: { wch: 10 }
                    },
                ],
                data: filteredData.map((item) => [
                    { value: item.facultyId },
                    { value: item.facultyName },
                    { value: item.departmentId },
                    { value: item.departmentName },
                    { value: item.studyProgramId },
                    { value: item.studyProgramBasicName },
                    { value: item.studyProgramName },
                    { value: item.studyCycleName },
                    { value: item.durationSemester + ' / ' + item.durationYear },
                    { value: item.language },
                ]),
            },
        ];

        return (
            <div>
                <Form>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <Form.Dropdown
                                label="Organizaciona jedinica"
                                placeholder="Odaberite organizacionu jedinicu"
                                fluid
                                search
                                selection
                                options={organizationalUnits}
                                value={selectedOrganizationalUnitId}
                                onChange={(e, { value }) =>
                                    this.setState({ selectedOrganizationalUnitId: value })
                                }
                            />
                        </Form.Field>

                        <Form.Field>
                            <Form.Dropdown
                                label="Ciklus studija"
                                placeholder="Odaberite cikluse"
                                fluid
                                multiple
                                search
                                selection
                                options={studyCycles}
                                value={selectedCycleIds}
                                onChange={(e, { value }) =>
                                    this.setState({ selectedCycleIds: value })
                                }
                            />
                        </Form.Field>
                    </Form.Group>

                    <Button
                        basic
                        color="blue"
                        floated="right"
                        onClick={this.props.closeReport}
                    >
                        Odustani
                    </Button>

                    <Button
                        color="blue"
                        floated="right"
                        disabled={
                            selectedCycleIds.length === 0 || !selectedOrganizationalUnitId
                        }
                        onClick={this.fetchData}
                    >
                        Prikaži izvještaj
                    </Button>
                </Form>

                <br/>
                <br/>
                <br/>

                {loading ? (
                    <Loader active inline="centered">
                        Učitavanje...
                    </Loader>
                ) : (
                    <Table celled compact selectable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell colSpan={7}>
                                    <Grid stackable>
                                        <Grid.Column width={14}>
                                            <SearchTableData
                                                profMozilla
                                                data={filteredData}
                                                getResults={this.getResults}
                                                filterItem={[
                                                    'facultyName',
                                                    'departmentName',
                                                    'studyProgramBasicName',
                                                    'studyProgramName',
                                                    'studyCycleName',
                                                    'language',
                                                ]}
                                            />
                                        </Grid.Column>
                                        <Grid.Column width={2} textAlign="right">
                                            <ExcelFile
                                                filename="StudijskiProgrami"
                                                element={
                                                    <Popup
                                                        trigger={
                                                            <Button
                                                                disabled={filteredData.length === 0}
                                                                animated="vertical"
                                                                color="green"
                                                                basic
                                                                floated="right"
                                                                fluid
                                                                style={{ textAlign: 'center' }}
                                                            >
                                                                <Button.Content hidden>
                                                                    <Icon name="download"/>
                                                                </Button.Content>
                                                                <Button.Content visible>Excel</Button.Content>
                                                            </Button>
                                                        }
                                                        content="Preuzmi spisak u Excel-u"
                                                        hideOnScroll
                                                        position="top center"
                                                    />
                                                }
                                            >
                                                <ExcelSheet
                                                    dataSet={sanitizeExcelDataSet(multiDataSet)}
                                                    name="StudijskiProgrami"
                                                />
                                            </ExcelFile>
                                        </Grid.Column>
                                    </Grid>
                                </Table.HeaderCell>
                            </Table.Row>

                            <Table.Row>
                                <Table.HeaderCell>Fakultet / akademija</Table.HeaderCell>
                                <Table.HeaderCell>Odsjek</Table.HeaderCell>
                                <Table.HeaderCell>ID nastavnog plana i programa</Table.HeaderCell>
                                <Table.HeaderCell>Studijski program - NPP</Table.HeaderCell>
                                <Table.HeaderCell>Ciklus studija</Table.HeaderCell>
                                <Table.HeaderCell>
                                    Trajanje (Semestar / Godina)
                                </Table.HeaderCell>
                                <Table.HeaderCell>Jezik</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {filteredData.length > 0 ? (
                                currentPosts.map((item, index) => (
                                    <Table.Row key={index}>
                                        <Table.Cell>{item.facultyName}</Table.Cell>
                                        <Table.Cell>{item.departmentName}</Table.Cell>
                                        <Table.Cell>{item.studyProgramId}</Table.Cell>
                                        <Table.Cell>
                                            {item.studyProgramBasicName} - {item.studyProgramName}
                                        </Table.Cell>
                                        <Table.Cell>{item.studyCycleName}</Table.Cell>
                                        <Table.Cell>
                                            {item.durationSemester} / {item.durationYear}
                                        </Table.Cell>
                                        <Table.Cell>{item.language}</Table.Cell>
                                    </Table.Row>
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={7}>
                                        <FormattedMessage
                                            id="common.noDataToDisplay"
                                            defaultMessage="Nema podataka za prikaz"
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                )}

                {/* Paginacija */}
                {filteredData.length > postsPerPage && (
                    <PaginationFront
                        postsPerPage={postsPerPage}
                        totalPosts={filteredData.length}
                        paginate={this.paginate}
                        activePage={currentPage}
                    />
                )}
            </div>
        );
    }
}

export default StudyProgramsByCycle;



// WEBPACK FOOTER //
// ./src/views/StudentService/StudentsReports/StudyProgramsByStudyCyclesForFaculty.js